import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BuildingDetailsFloorsAddFloor from './BuildingDetailsFloorsAddFloor';

const BuildingDetailsFloors = () => {
    const { id } = useParams();
    const [floors, setFloors] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [editingFloor, setEditingFloor] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    const fetchFloors = () => {
        axios.get(`/buildings/${id}/floor/`)
            .then(res => setFloors(res.data))
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to fetch floors.', severity: 'error' });
            });
    };

    useEffect(() => {
        fetchFloors();
    }, [id]);

    const handleModalOpen = () => setModalOpen(true);

    const paginatedFloors = floors.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const totalPages = Math.ceil(floors.length / itemsPerPage);

    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Floors
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleModalOpen}
                >
                    Add Floor
                </Button>
            </Box>

            {paginatedFloors.map((floor) => (
                <Accordion
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: 'none',
                        border: '1px solid #e0e0e0',
                        '&::before': { display: 'none' }
                    }}
                    key={floor.id}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                            Floor #{floor.floor_level}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            sx={{ mr: 1 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                axios.get(`/buildings/${id}/floor/${floor.id}/`)
                                    .then(res => {
                                        setEditingFloor(res.data);
                                        setModalOpen(true);
                                    })
                                    .catch(() => {
                                        setSnackbar({ open: true, message: 'Failed to load floor for editing.', severity: 'error' });
                                    });
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            sx={{ mr: 2 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                axios.delete(`/buildings/${id}/floor/${floor.id}/`)
                                    .then(() => {
                                        setSnackbar({ open: true, message: 'Floor deleted successfully.', severity: 'success' });
                                        fetchFloors();
                                    })
                                    .catch(() => {
                                        setSnackbar({ open: true, message: 'Failed to delete floor.', severity: 'error' });
                                    });
                            }}
                        >
                            Delete
                        </Button>
                    </AccordionSummary>

                    <AccordionDetails sx={{ backgroundColor: '#f5f9f6' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Area (m²)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {floor.area}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">U-Value (Insulated)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {floor.u_value}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">U-Value (Uninsulated)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {floor.u_value_uninsulated}
                                </Typography>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setEditingFloor(null); }}>
                <BuildingDetailsFloorsAddFloor
                    buildingId={id}
                    floor={editingFloor}
                    onSuccess={() => {
                        fetchFloors();
                        setSnackbar({
                            open: true,
                            message: editingFloor ? 'Floor updated successfully!' : 'Floor added successfully!',
                            severity: 'success'
                        });
                        setEditingFloor(null);
                    }}
                    onError={() => {
                        setSnackbar({ open: true, message: 'Failed to save floor.', severity: 'error' });
                    }}
                    onClose={() => { setModalOpen(false); setEditingFloor(null); }}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuildingDetailsFloors;
